import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { PatientLookupParticipant, PatientLookupResponse } from 'src/app/models/patient-lookup.model';
import { FirebaseIdTokenService } from 'src/app/services/auth/firebase-id-token.service';
import { environment } from 'src/environments/environment';

/**
 * Resolves an inbound caller's phone number to a patient via the call-processor service.
 *
 * Used to decide whether the in-progress call UI can offer a "go to chart" action. A failed
 * lookup (network error or no match) resolves to `null` so the caller can treat it as
 * "not associated" rather than surfacing an error to the nurse mid-call.
 */
@Injectable({
  providedIn: 'root',
})
export class PatientLookupService {
  // The lookup lives in the call-processor service, reached via call_transcriptions_endpoint
  // (markusApiUrl points to a different service in some environments). Matches CallsDashboardService.
  private baseUrl = `${environment.call_transcriptions_endpoint}/api/v1`;

  constructor(private http: HttpClient, private firebaseIdToken: FirebaseIdTokenService) {}

  /**
   * Returns the matched patient, or null when the number is not associated / lookup fails.
   *
   * The route is guarded by the call-processor's `AuthGuard`, which verifies a **Firebase ID
   * token** — not the Welby JWT that `AuthInterceptor` attaches to welbyEndpoint calls. This runs
   * the moment an inbound call is answered, so getting the token wrong here is not a degraded
   * feature: it used to end the care manager's session and drop the live call.
   */
  lookupByPhone(phoneNumber: string): Observable<PatientLookupParticipant | null> {
    return this.firebaseIdToken.authHeaders().pipe(
      switchMap((headers) =>
        this.http.post<PatientLookupResponse>(`${this.baseUrl}/calls/patient/lookup`, { phoneNumber }, { headers })
      ),
      map((res) => (res?.found ? res.patientId : null)),
      catchError((err) => {
        console.error('[PatientLookup] Failed to resolve caller number', err);
        return of(null);
      })
    );
  }
}
